import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { UserPlus, Key, Trash2, Settings2, Copy, Check, RefreshCw, ShoppingBag, Power } from "lucide-react";
import type { Seller } from "@shared/schema";

function generatePassword(length = 8): string {
  const chars = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789";
  const array = new Uint32Array(length);
  crypto.getRandomValues(array);
  let password = "";
  for (let i = 0; i < length; i++) {
    password += chars.charAt(array[i] % chars.length);
  }
  return password;
}

type SellerWithCreds = Seller & { hasCredentials: boolean; username: string | null };

const newSellerSchema = z.object({
  name: z.string().min(1, "Namn krävs"),
});

const credentialsSchema = z.object({
  username: z.string().min(3, "Minst 3 tecken"),
  password: z.string().min(10, "Minst 10 tecken"),
});

type NewSellerData = z.infer<typeof newSellerSchema>;
type CredentialsData = z.infer<typeof credentialsSchema>;

export default function AdminSellers() {
  const { toast } = useToast();
  const [newSellerOpen, setNewSellerOpen] = useState(false);
  const [credDialogOpen, setCredDialogOpen] = useState(false);
  const [credSeller, setCredSeller] = useState<SellerWithCreds | null>(null);
  const [permDialogOpen, setPermDialogOpen] = useState(false);
  const [permSeller, setPermSeller] = useState<Seller | null>(null);
  const [savedCreds, setSavedCreds] = useState<{ username: string; password: string; sellerName: string } | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { data: sellers, isLoading } = useQuery<SellerWithCreds[]>({ queryKey: ["/api/admin/sellers"] });

  const { data: portalStatus } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/admin/sellers/portal-status"],
  });

  const portalToggleMutation = useMutation({
    mutationFn: (enabled: boolean) => apiRequest("POST", "/api/admin/sellers/portal-status", { enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/sellers/portal-status"] });
    },
    onError: () => toast({ title: "Fel", description: "Kunde inte uppdatera portalstatus.", variant: "destructive" }),
  });

  const newSellerForm = useForm<NewSellerData>({
    resolver: zodResolver(newSellerSchema),
    defaultValues: { name: "" },
  });

  const credForm = useForm<CredentialsData>({
    resolver: zodResolver(credentialsSchema),
    defaultValues: { username: "", password: generatePassword() },
  });

  const createMutation = useMutation({
    mutationFn: (data: NewSellerData) => apiRequest("POST", "/api/admin/sellers", data),
    onSuccess: () => {
      toast({ title: "Säljare skapad!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/sellers"] });
      setNewSellerOpen(false);
      newSellerForm.reset();
    },
    onError: () => toast({ title: "Fel", description: "Kunde inte skapa säljare.", variant: "destructive" }),
  });

  const credMutation = useMutation({
    mutationFn: async (data: CredentialsData & { sellerId: string }) => {
      const response = await apiRequest("POST", `/api/admin/sellers/${data.sellerId}/credentials`, data);
      return { ...response, submittedPassword: data.password };
    },
    onSuccess: (_, variables) => {
      setSavedCreds({
        username: variables.username,
        password: variables.password,
        sellerName: credSeller?.name ?? "",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/sellers"] });
      setCredDialogOpen(false);
      setCredSeller(null);
    },
    onError: async (error: any) => {
      toast({ title: "Fel", description: error?.message ?? "Kunde inte spara inloggningsuppgifter.", variant: "destructive" });
    },
  });

  const permMutation = useMutation({
    mutationFn: (data: { sellerId: string; canRegisterClients: boolean; canCreateOrders: boolean; canViewOrders: boolean }) =>
      apiRequest("PATCH", `/api/admin/sellers/${data.sellerId}/permissions`, {
        canRegisterClients: data.canRegisterClients,
        canCreateOrders: data.canCreateOrders,
        canViewOrders: data.canViewOrders,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/sellers"] });
    },
    onError: () => toast({ title: "Fel", description: "Kunde inte spara behörigheter.", variant: "destructive" }),
  });

  const togglePerm = (key: keyof typeof permState, value: boolean) => {
    if (!permSeller) return;
    const next = { ...permState, [key]: value };
    setPermState(next);
    permMutation.mutate({ sellerId: permSeller.id, ...next });
  };

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/admin/sellers/${id}`),
    onSuccess: () => {
      toast({ title: "Säljare borttagen!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/sellers"] });
      setDeletingId(null);
    },
    onError: async (error: any) => {
      toast({ title: "Fel", description: error?.message ?? "Kunde inte ta bort säljare.", variant: "destructive" });
      setDeletingId(null);
    },
  });

  const openCredentials = async (seller: Seller) => {
    try {
      const response = await fetch(`/api/admin/sellers/${seller.id}`, { credentials: "include" });
      const data = await response.json() as SellerWithCreds;
      setCredSeller(data);
      credForm.reset({
        username: data.username ?? "",
        password: generatePassword(),
      });
    } catch {
      setCredSeller({ ...seller, hasCredentials: false, username: null });
      credForm.reset({ username: "", password: generatePassword() });
    }
    setCredDialogOpen(true);
  };

  const openPermissions = (seller: Seller) => {
    setPermSeller(seller);
    setPermDialogOpen(true);
  };

  const copyField = (value: string, field: string) => {
    navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const [permState, setPermState] = useState({ canRegisterClients: true, canCreateOrders: true, canViewOrders: true });

  const handleOpenPermDialog = (seller: Seller) => {
    setPermState({
      canRegisterClients: seller.canRegisterClients ?? true,
      canCreateOrders: seller.canCreateOrders ?? true,
      canViewOrders: seller.canViewOrders ?? true,
    });
    openPermissions(seller);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">Säljare</h1>
          <p className="text-muted-foreground">Hantera säljare, deras inloggning och behörigheter</p>
        </div>
        <Button onClick={() => setNewSellerOpen(true)} data-testid="button-add-seller">
          <UserPlus className="h-4 w-4 mr-2" />
          Ny säljare
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Power className={`h-5 w-5 ${portalStatus?.enabled !== false ? "text-green-600" : "text-muted-foreground"}`} />
              <div>
                <p className="font-medium">Säljportal</p>
                <p className="text-sm text-muted-foreground">
                  {portalStatus?.enabled !== false ? "Portalen är öppen — säljare kan logga in" : "Portalen är stängd — inloggning blockerad"}
                </p>
              </div>
            </div>
            <Switch
              checked={portalStatus?.enabled !== false}
              onCheckedChange={(v) => portalToggleMutation.mutate(v)}
              disabled={portalToggleMutation.isPending || portalStatus === undefined}
              data-testid="switch-seller-portal-enabled"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Säljarelista</CardTitle>
          <CardDescription>Alla säljare i systemet</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">{[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : sellers && sellers.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Namn</TableHead>
                    <TableHead>Behörigheter</TableHead>
                    <TableHead>Inloggning</TableHead>
                    <TableHead className="text-right">Åtgärd</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sellers.map(seller => (
                    <TableRow key={seller.id} data-testid={`row-seller-${seller.id}`}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <ShoppingBag className="h-4 w-4 text-muted-foreground" />
                          {seller.name}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {seller.canRegisterClients && (
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">Registrera kunder</Badge>
                          )}
                          {seller.canCreateOrders && (
                            <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">Skapa ordrar</Badge>
                          )}
                          {seller.canViewOrders && (
                            <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">Se ordrar</Badge>
                          )}
                          {!seller.canRegisterClients && !seller.canCreateOrders && !seller.canViewOrders && (
                            <Badge variant="secondary" className="text-xs">Inga behörigheter</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={`text-xs ${seller.hasCredentials ? "bg-green-50 text-green-700 border-green-200" : "bg-gray-50 text-gray-500 border-gray-200"}`}
                        >
                          {seller.hasCredentials ? "Konfigurerad" : "Ej konfigurerad"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="icon" variant="ghost" onClick={() => openCredentials(seller)} title="Inloggningsuppgifter" data-testid={`button-credentials-${seller.id}`}>
                            <Key className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => handleOpenPermDialog(seller)} title="Behörigheter" data-testid={`button-permissions-${seller.id}`}>
                            <Settings2 className="h-4 w-4" />
                          </Button>
                          <AlertDialog open={deletingId === seller.id} onOpenChange={open => !open && setDeletingId(null)}>
                            <AlertDialogTrigger asChild>
                              <Button size="icon" variant="ghost" className="text-destructive" onClick={() => setDeletingId(seller.id)} data-testid={`button-delete-seller-${seller.id}`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Ta bort säljare?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Säljare som är tilldelade kunder kan inte tas bort. Denna åtgärd kan inte ångras.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Avbryt</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => deleteMutation.mutate(seller.id)}
                                  disabled={deleteMutation.isPending}
                                  className="bg-destructive text-destructive-foreground"
                                >
                                  Ta bort
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="py-12 text-center">
              <ShoppingBag className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Inga säljare registrerade ännu</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New seller dialog */}
      <Dialog open={newSellerOpen} onOpenChange={setNewSellerOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Ny säljare</DialogTitle>
          </DialogHeader>
          <Form {...newSellerForm}>
            <form onSubmit={newSellerForm.handleSubmit(d => createMutation.mutate(d))} className="space-y-4">
              <FormField control={newSellerForm.control} name="name" render={({ field }) => (
                <FormItem>
                  <FormLabel>Namn</FormLabel>
                  <FormControl><Input {...field} placeholder="Säljarens namn" autoFocus data-testid="input-seller-name" /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setNewSellerOpen(false)}>Avbryt</Button>
                <Button type="submit" disabled={createMutation.isPending} data-testid="button-confirm-add-seller">
                  {createMutation.isPending ? "Skapar..." : "Skapa"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Credentials dialog */}
      <Dialog open={credDialogOpen} onOpenChange={setCredDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Inloggningsuppgifter – {credSeller?.name}</DialogTitle>
            <DialogDescription>
              {credSeller?.hasCredentials ? "Uppdatera säljares inloggning" : "Sätt upp inloggning för säljportalen"}
            </DialogDescription>
          </DialogHeader>
          <Form {...credForm}>
            <form onSubmit={credForm.handleSubmit(d => credSeller && credMutation.mutate({ ...d, sellerId: credSeller.id }))} className="space-y-4">
              <FormField control={credForm.control} name="username" render={({ field }) => (
                <FormItem>
                  <FormLabel>Användarnamn</FormLabel>
                  <FormControl><Input {...field} data-testid="input-cred-username" /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={credForm.control} name="password" render={({ field }) => (
                <FormItem>
                  <FormLabel>Lösenord</FormLabel>
                  <div className="flex gap-2">
                    <FormControl><Input {...field} data-testid="input-cred-password" /></FormControl>
                    <Button type="button" variant="outline" size="icon" onClick={() => credForm.setValue("password", generatePassword())} title="Generera lösenord" data-testid="button-generate-password">
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                  <FormMessage />
                </FormItem>
              )} />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCredDialogOpen(false)}>Avbryt</Button>
                <Button type="submit" disabled={credMutation.isPending} data-testid="button-save-credentials">
                  {credMutation.isPending ? "Sparar..." : "Spara"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Permissions dialog */}
      <Dialog open={permDialogOpen} onOpenChange={setPermDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Behörigheter – {permSeller?.name}</DialogTitle>
            <DialogDescription>Välj vad säljaren får göra i säljportalen.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <p className="font-medium text-sm">Registrera kunder</p>
                <p className="text-xs text-muted-foreground">Säljaren kan lägga till nya kunder</p>
              </div>
              <Switch
                checked={permState.canRegisterClients}
                onCheckedChange={v => togglePerm("canRegisterClients", v)}
                disabled={permMutation.isPending}
                data-testid="switch-can-register-clients"
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <p className="font-medium text-sm">Skapa ordrar</p>
                <p className="text-xs text-muted-foreground">Säljaren kan lägga ordrar för kunder</p>
              </div>
              <Switch
                checked={permState.canCreateOrders}
                onCheckedChange={v => togglePerm("canCreateOrders", v)}
                disabled={permMutation.isPending}
                data-testid="switch-can-create-orders"
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <p className="font-medium text-sm">Se ordrar</p>
                <p className="text-xs text-muted-foreground">Säljaren kan se dagens ordrar från sina kunder</p>
              </div>
              <Switch
                checked={permState.canViewOrders}
                onCheckedChange={v => togglePerm("canViewOrders", v)}
                disabled={permMutation.isPending}
                data-testid="switch-can-view-orders"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermDialogOpen(false)} data-testid="button-close-permissions">Stäng</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Saved credentials confirmation */}
      {savedCreds && (
        <Dialog open={!!savedCreds} onOpenChange={() => setSavedCreds(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Inloggningsuppgifter sparade</DialogTitle>
              <DialogDescription>
                Spara dessa uppgifter för {savedCreds.sellerName}. Lösenordet visas inte igen.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="rounded-md bg-muted p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Användarnamn</p>
                    <p className="font-mono text-sm" data-testid="text-saved-username">{savedCreds.username}</p>
                  </div>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => copyField(savedCreds.username, "username")} data-testid="button-copy-username">
                    {copiedField === "username" ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                  </Button>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Lösenord</p>
                    <p className="font-mono text-sm" data-testid="text-saved-password">{savedCreds.password}</p>
                  </div>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => copyField(savedCreds.password, "password")} data-testid="button-copy-password">
                    {copiedField === "password" ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Säljportalen nås via <span className="font-mono">/seller</span></p>
            </div>
            <DialogFooter>
              <Button onClick={() => setSavedCreds(null)} data-testid="button-close-saved-creds">Stäng</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
